import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Loader2, Printer } from 'lucide-react';
import { AdminAttemptReport, AdminAttemptReportSection, assessmentAnalyticsService } from '../../services/api';

const BRAND_LOGO_SRC = '/assets/logo-mane-haghighi-asli-1 (1).png';
const MAX = 25;

const fa = (n: number | string) => Number(n).toLocaleString('fa-IR');
const faDecimal = (n: number) => n.toLocaleString('fa-IR', { maximumFractionDigits: 1 });

const PRIORITY = {
  HIGH: {
    color: '#c62828',
    bg: '#fdecec',
    border: '#f3b8b8',
    label: 'اولویت بالا',
    verdict: 'نیازمند توجه فوری',
    narrative:
      'این حوزه بیشترین فاصله را با وضعیت مطلوب دارد. پیشنهاد می‌شود نخستین گام مسیر رشد، کار متمرکز روی همین حوزه باشد؛ چون بهبود آن معمولاً اثر زنجیره‌ای بر سایر حوزه‌ها دارد.',
  },
  MEDIUM: {
    color: '#b8770a',
    bg: '#fff6e0',
    border: '#f4d78a',
    label: 'اولویت متوسط',
    verdict: 'قابل بهبود',
    narrative:
      'پایه‌ای در این حوزه شکل گرفته اما هنوز ناپایدار است. با تمرین هدفمند و پیوسته، این حوزه در کوتاه‌مدت قابل تقویت است.',
  },
  LOW: {
    color: '#1b7f3b',
    bg: '#e8f6ec',
    border: '#a9dcb8',
    label: 'نقطهٔ قوت',
    verdict: 'وضعیت مطلوب',
    narrative:
      'این حوزه در وضعیت خوبی قرار دارد و می‌تواند تکیه‌گاهی برای کار روی حوزه‌های نیازمند بهبود باشد.',
  },
} as const;

function fullName(u: AdminAttemptReport['user']) {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || 'بدون نام';
}

function faDate(iso: string | null | undefined, withTime = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

function durationLabel(start: string, end: string | null) {
  if (!end) return null;
  const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  if (minutes < 1) return 'کمتر از یک دقیقه';
  if (minutes < 60) return `${fa(minutes)} دقیقه`;
  return `${fa(Math.floor(minutes / 60))} ساعت و ${fa(minutes % 60)} دقیقه`;
}

/** فاصله با میانگین هم‌گروهی‌ها به زبان گزارش */
function cohortVerdict(section: AdminAttemptReportSection) {
  if (section.cohortAverageScore25 == null) return null;
  const diff = section.score25 - section.cohortAverageScore25;
  if (diff <= -3) return { text: 'پایین‌تر از میانگین هم‌گروهی‌ها', tone: 'HIGH' as const, diff };
  if (diff >= 3) return { text: 'بالاتر از میانگین هم‌گروهی‌ها', tone: 'LOW' as const, diff };
  return { text: 'هم‌سطح میانگین هم‌گروهی‌ها', tone: 'MEDIUM' as const, diff };
}

const AssessmentReportPrint: React.FC = () => {
  const { attemptId = '' } = useParams<{ attemptId: string }>();
  const [report, setReport] = useState<AdminAttemptReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await assessmentAnalyticsService.attemptReport(attemptId);
        if (alive) setReport(data);
      } catch (err: any) {
        if (alive) setError(err?.response?.data?.message || 'دریافت گزارش انجام نشد.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [attemptId]);

  useEffect(() => {
    if (!report) return;
    const previous = document.title;
    document.title = `گزارش مسیر رشد — ${fullName(report.user)}`;
    return () => {
      document.title = previous;
    };
  }, [report]);

  const narrative = useMemo(() => {
    if (!report) return '';
    const { summary, sections } = report;
    const high = sections.filter((s) => s.priority === 'HIGH');
    const strengths = sections.filter((s) => s.priority === 'LOW');
    const parts: string[] = [];
    parts.push(
      `در این ارزیابی ${fa(summary.sectionCount)} حوزه از طریق ${fa(summary.questionCount)} سوال سنجیده شد و امتیاز کلی ${fa(
        summary.overallPercent,
      )}٪ به دست آمد.`,
    );
    if (high.length) {
      const names = high
        .slice(0, 3)
        .map((s) => `«${s.title}»`)
        .join('، ');
      parts.push(
        `${fa(high.length)} حوزه در سطح اولویت بالا قرار گرفت که بیشترین نیاز به توجه در ${names} دیده می‌شود.`,
      );
    } else {
      parts.push('هیچ حوزه‌ای در سطح اولویت بالا قرار نگرفت که نشان‌دهندهٔ توازن نسبی در وضعیت فعلی است.');
    }
    if (strengths.length) {
      const top = strengths[strengths.length - 1];
      parts.push(`نقطهٔ قوت اصلی، حوزهٔ «${top.title}» با امتیاز ${fa(top.score25)} از ${fa(MAX)} است.`);
    }
    return parts.join(' ');
  }, [report]);

  if (error) {
    return (
      <div className="report-shell" dir="rtl">
        <ReportStyles />
        <div className="report-toolbar">
          <Link to="/assessments" className="toolbar-link">
            <ArrowRight className="h-4 w-4" />
            بازگشت به پرسشنامه‌ها
          </Link>
        </div>
        <p className="report-error">{error}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="report-shell" dir="rtl">
        <ReportStyles />
        <div className="report-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
          در حال آماده‌سازی گزارش…
        </div>
      </div>
    );
  }

  const { summary, sections, user, workshop, assessment, cohort } = report;
  const recommended = sections.filter((s) => s.course && s.priority !== 'LOW');
  const generatedAt = new Date().toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const reportCode = report.attemptId.slice(-8).toUpperCase();
  const duration = durationLabel(report.startedAt, report.completedAt);

  return (
    <div className="report-shell" dir="rtl">
      <ReportStyles />

      <div className="report-toolbar no-print">
        <Link to="/assessments" className="toolbar-link">
          <ArrowRight className="h-4 w-4" />
          بازگشت
        </Link>
        <span className="toolbar-hint">
          برای ذخیره به‌صورت PDF، در پنجرهٔ چاپ گزینهٔ «Save as PDF» را انتخاب کنید.
        </span>
        <button type="button" className="toolbar-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" />
          چاپ / ذخیره PDF
        </button>
      </div>

      <article className="report-page">
        {/* ── سربرگ ─────────────────────────────────────────────── */}
        <header className="report-header">
          <div className="header-brand">
            <img src={BRAND_LOGO_SRC} alt="من حقیقی" className="brand-logo" />
            <div>
              <p className="brand-kicker">گزارش تحلیلی فردی</p>
              <h1 className="report-title">{assessment.title}</h1>
            </div>
          </div>
          <div className="header-meta">
            <div className="meta-row">
              <span className="meta-key">کد گزارش</span>
              <span className="meta-val mono">{reportCode}</span>
            </div>
            <div className="meta-row">
              <span className="meta-key">تاریخ تکمیل</span>
              <span className="meta-val">{faDate(report.completedAt, true)}</span>
            </div>
            <div className="meta-row">
              <span className="meta-key">صدور گزارش</span>
              <span className="meta-val">{generatedAt}</span>
            </div>
          </div>
        </header>

        {/* ── مشخصات پاسخ‌دهنده ────────────────────────────────── */}
        <section className="respondent-card">
          <div className="respondent-main">
            <p className="respondent-label">پاسخ‌دهنده</p>
            <h2 className="respondent-name">{fullName(user)}</h2>
            <p className="respondent-contact">
              {user.phone ? <span dir="ltr">{user.phone}</span> : null}
              {user.phone && user.email ? <span className="dot">·</span> : null}
              {user.email ? <span dir="ltr">{user.email}</span> : null}
              {!user.phone && !user.email ? '—' : null}
            </p>
          </div>
          <dl className="respondent-facts">
            <div>
              <dt>کارگاه</dt>
              <dd>{workshop?.title ?? 'بدون کارگاه'}</dd>
            </div>
            <div>
              <dt>مدت پاسخ‌گویی</dt>
              <dd>{duration ?? '—'}</dd>
            </div>
            <div>
              <dt>پاسخ‌ها</dt>
              <dd>
                {fa(summary.answeredCount)} از {fa(summary.questionCount)} سوال
              </dd>
            </div>
            <div>
              <dt>گروه مقایسه</dt>
              <dd>
                {fa(cohort.respondentCount)} نفر
                <span className="muted"> ({cohort.scope === 'workshop' ? 'همین کارگاه' : 'همهٔ پاسخ‌دهندگان'})</span>
              </dd>
            </div>
          </dl>
        </section>

        {/* ── خلاصهٔ مدیریتی ──────────────────────────────────── */}
        <section className="summary-grid">
          <div className="kpi kpi-overall">
            <Gauge percent={summary.overallPercent} />
            <div>
              <p className="kpi-label">امتیاز کلی</p>
              <p className="kpi-value">{fa(summary.overallPercent)}٪</p>
              <p className="kpi-sub">میانگین حوزه‌ها: {fa(summary.averageScore25)} از {fa(MAX)}</p>
            </div>
          </div>
          <KpiTile tone="HIGH" value={summary.highPriorityCount} label="حوزه با اولویت بالا" />
          <KpiTile tone="MEDIUM" value={summary.mediumPriorityCount} label="حوزهٔ قابل بهبود" />
          <KpiTile tone="LOW" value={summary.lowPriorityCount} label="نقطهٔ قوت" />
        </section>

        <section className="narrative">
          <h3 className="section-heading">خلاصهٔ تحلیلی</h3>
          <p>{narrative}</p>
        </section>

        {/* ── نیم‌رخ اولویت‌ها ────────────────────────────────── */}
        <section className="profile">
          <div className="profile-head">
            <h3 className="section-heading">نیم‌رخ حوزه‌ها به ترتیب اولویت</h3>
            <div className="legend">
              <span className="legend-item">
                <i style={{ background: PRIORITY.HIGH.color }} /> اولویت بالا (۰–۸)
              </span>
              <span className="legend-item">
                <i style={{ background: PRIORITY.MEDIUM.color }} /> متوسط (۹–۱۶)
              </span>
              <span className="legend-item">
                <i style={{ background: PRIORITY.LOW.color }} /> نقطهٔ قوت (۱۷–۲۵)
              </span>
              <span className="legend-item">
                <i className="legend-marker" /> میانگین هم‌گروهی‌ها
              </span>
            </div>
          </div>
          <p className="profile-hint">
            امتیاز هر حوزه روی بازهٔ ۰ تا ۲۵ نرمال شده است؛ امتیاز کمتر یعنی نیاز بیشتر به توجه.
          </p>
          <div className="profile-rows">
            {sections.map((s) => {
              const p = PRIORITY[s.priority];
              const width = Math.max(2, (s.score25 / MAX) * 100);
              const avg = s.cohortAverageScore25;
              return (
                <div className="profile-row" key={s.sectionId}>
                  <span className="row-rank">{fa(s.rank)}</span>
                  <span className="row-title">{s.title}</span>
                  <div className="row-bar">
                    <div className="row-bar-fill" style={{ width: `${width}%`, background: p.color }} />
                    {avg != null ? (
                      <span
                        className="row-bar-marker"
                        style={{ right: `${(avg / MAX) * 100}%` }}
                        title={`میانگین: ${faDecimal(avg)}`}
                      />
                    ) : null}
                  </div>
                  <span className="row-score" style={{ color: p.color }}>
                    {fa(s.score25)}
                  </span>
                  <span className="row-badge" style={{ background: p.bg, color: p.color, borderColor: p.border }}>
                    {p.label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── تحلیل تفصیلی هر حوزه ──────────────────────────── */}
        <section className="details">
          <h3 className="section-heading details-heading">تحلیل تفصیلی حوزه‌ها</h3>
          {sections.map((s) => (
            <SectionBlock key={s.sectionId} section={s} optionLabels={assessment.optionLabels} />
          ))}
        </section>

        {/* ── پاسخ‌های بدون امتیاز (مثل سوال‌های آمادگی برای تغییر) ──── */}
        {report.extraSections?.length ? (
          <section className="details">
            <h3 className="section-heading details-heading">پاسخ‌های تکمیلی (بدون امتیاز)</h3>
            {report.extraSections.map((s) => (
              <div
                key={s.sectionId}
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10 }}
              >
                <div style={{ padding: '10px 12px 0' }}>
                  <p className="section-title">{s.title}</p>
                </div>
                <div className="section-body">
                  <table className="answers">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}>#</th>
                        <th>سوال</th>
                        <th style={{ width: 72 }}>پاسخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.questions.map((q, index) => (
                        <tr key={q.id}>
                          <td>{fa(index + 1)}</td>
                          <td>{q.text}</td>
                          <td style={{ fontWeight: 800 }}>{q.label ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>
        ) : null}

        {/* ── پیشنهادها ──────────────────────────────────────── */}
        {recommended.length ? (
          <section className="recommend">
            <h3 className="section-heading">مسیر پیشنهادی رشد</h3>
            <p className="recommend-hint">
              بر اساس اولویت‌های شناسایی‌شده، این ترتیب برای شروع پیشنهاد می‌شود:
            </p>
            <ol className="recommend-list">
              {recommended.map((s, i) => {
                const p = PRIORITY[s.priority];
                return (
                  <li key={s.sectionId}>
                    <span className="recommend-step">{fa(i + 1)}</span>
                    <div className="recommend-body">
                      <p className="recommend-course">{s.course!.title}</p>
                      <p className="recommend-why">
                        برای تقویت حوزهٔ «{s.title}»
                        <span className="recommend-tag" style={{ color: p.color, background: p.bg, borderColor: p.border }}>
                          {p.label} · امتیاز {fa(s.score25)}
                        </span>
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ) : null}

        <footer className="report-footer">
          <span>من حقیقی · {assessment.title}</span>
          <span>{fullName(user)} · کد {reportCode}</span>
          <span>این گزارش محرمانه است و صرفاً برای استفادهٔ پاسخ‌دهنده و مربی تهیه شده.</span>
        </footer>
      </article>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */

const KpiTile: React.FC<{ tone: keyof typeof PRIORITY; value: number; label: string }> = ({
  tone,
  value,
  label,
}) => {
  const p = PRIORITY[tone];
  return (
    <div className="kpi" style={{ borderColor: p.border, background: p.bg }}>
      <p className="kpi-value" style={{ color: p.color }}>
        {fa(value)}
      </p>
      <p className="kpi-label">{label}</p>
    </div>
  );
};

const Gauge: React.FC<{ percent: number }> = ({ percent }) => {
  const r = 30;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const tone = clamped <= 32 ? PRIORITY.HIGH : clamped <= 64 ? PRIORITY.MEDIUM : PRIORITY.LOW;
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" className="gauge" aria-hidden="true">
      <circle cx="42" cy="42" r={r} fill="none" stroke="#e8ebf1" strokeWidth="9" />
      <circle
        cx="42"
        cy="42"
        r={r}
        fill="none"
        stroke={tone.color}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={`${(clamped / 100) * c} ${c}`}
        transform="rotate(-90 42 42)"
      />
    </svg>
  );
};

const SectionBlock: React.FC<{ section: AdminAttemptReportSection; optionLabels: string[] }> = ({
  section: s,
  optionLabels,
}) => {
  const p = PRIORITY[s.priority];
  const cohort = cohortVerdict(s);
  return (
    <div className="section-block">
      <div className="section-head" style={{ borderColor: p.border }}>
        <span className="section-rank" style={{ background: p.color }}>
          {fa(s.rank)}
        </span>
        <div className="section-head-main">
          <h4 className="section-title">{s.title}</h4>
          <p className="section-verdict" style={{ color: p.color }}>
            {p.verdict}
            {cohort ? (
              <>
                <span className="dot">·</span>
                <span style={{ color: PRIORITY[cohort.tone].color }}>{cohort.text}</span>
                <span className="muted">
                  {' '}
                  ({cohort.diff > 0 ? '+' : ''}
                  {faDecimal(cohort.diff)})
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="section-scores">
          <div className="score-main" style={{ color: p.color }}>
            {fa(s.score25)}
            <span className="score-max">/{fa(MAX)}</span>
          </div>
          <div className="score-sub">
            امتیاز خام {fa(s.rawScore)} از {fa(s.maxScore)}
            {s.cohortAverageScore25 != null ? (
              <>
                <br />
                میانگین هم‌گروهی‌ها {faDecimal(s.cohortAverageScore25)}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="section-body">
        {/* پرسشنامهٔ تحلیلی متن اختصاصی همان سطح را دارد؛ وگرنه متن عمومی سطح اولویت */}
        <p className="section-narrative">{s.analysis || p.narrative}</p>
        {s.description ? <p className="section-desc">{s.description}</p> : null}
        {s.course ? (
          <p className="section-course">
            <span className="section-course-key">دورهٔ پیشنهادی:</span> {s.course.title}
          </p>
        ) : null}

        <table className="answers">
          <thead>
            <tr>
              <th className="col-idx">#</th>
              <th>گویه</th>
              <th className="col-answer">پاسخ</th>
              <th className="col-score">امتیاز</th>
            </tr>
          </thead>
          <tbody>
            {s.questions.map((q, i) => {
              const score = q.effectiveScore ?? 0;
              return (
                <tr key={q.id}>
                  <td className="col-idx">{fa(i + 1)}</td>
                  <td className="q-text">
                    {q.text}
                    {q.reverseScored ? <span className="q-reverse" title="گویهٔ مثبت — امتیاز معکوس">↺</span> : null}
                  </td>
                  <td className="col-answer">{q.label ?? '—'}</td>
                  <td className="col-score">
                    <span className="dots" aria-label={`${score} از ۵`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <i
                          key={n}
                          style={{
                            background: n <= score ? scoreColor(score) : '#e3e6ec',
                          }}
                        />
                      ))}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function scoreColor(score: number) {
  if (score <= 2) return PRIORITY.HIGH.color;
  if (score <= 3) return PRIORITY.MEDIUM.color;
  return PRIORITY.LOW.color;
}

/* ────────────────────────────────────────────────────────────────────────── */

const ReportStyles: React.FC = () => (
  <style>{`
    .report-shell {
      --ink: #14213d;
      --ink-2: #4a5568;
      --ink-3: #8a94a6;
      --line: #e3e7ee;
      --brand: #1c3b9b;
      --paper: #ffffff;
      min-height: 100vh;
      background: #eef1f6;
      color: var(--ink);
      font-family: 'Vazirmatn', 'Segoe UI', Tahoma, sans-serif;
      line-height: 1.7;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .report-shell * { box-sizing: border-box; }
    .mono { font-family: ui-monospace, 'Courier New', monospace; letter-spacing: .04em; }
    .muted { color: var(--ink-3); }
    .dot { margin: 0 .35em; color: var(--ink-3); }

    .report-toolbar {
      position: sticky; top: 0; z-index: 10;
      display: flex; align-items: center; gap: 12px;
      padding: 10px 20px; background: #fff; border-bottom: 1px solid var(--line);
      font-size: 13px; font-weight: 700;
    }
    .toolbar-link { display: inline-flex; align-items: center; gap: 6px; color: var(--ink-2); text-decoration: none; }
    .toolbar-hint { margin-inline-start: auto; color: var(--ink-3); font-weight: 600; font-size: 12px; }
    .toolbar-print {
      display: inline-flex; align-items: center; gap: 8px;
      background: var(--brand); color: #fff; border: 0; border-radius: 10px;
      padding: 9px 16px; font: inherit; font-weight: 800; cursor: pointer;
    }
    .report-loading, .report-error {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 80px 20px; font-weight: 700; color: var(--ink-2);
    }
    .report-error { color: #c62828; }

    .report-page {
      width: 210mm; min-height: 297mm; margin: 24px auto;
      padding: 16mm 14mm 22mm;
      background: var(--paper); box-shadow: 0 10px 40px -20px rgba(20,33,61,.35);
      font-size: 11.5px;
    }

    /* سربرگ */
    .report-header {
      display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
      padding-bottom: 12px; border-bottom: 2px solid var(--brand);
    }
    .header-brand { display: flex; align-items: center; gap: 14px; }
    .brand-logo { height: 46px; width: auto; }
    .brand-kicker { margin: 0; font-size: 10.5px; font-weight: 800; color: var(--brand); letter-spacing: .02em; }
    .report-title { margin: 2px 0 0; font-size: 19px; font-weight: 900; color: var(--ink); }
    .header-meta { display: grid; gap: 3px; font-size: 10.5px; min-width: 190px; }
    .meta-row { display: flex; justify-content: space-between; gap: 12px; }
    .meta-key { color: var(--ink-3); font-weight: 700; }
    .meta-val { font-weight: 800; }

    /* پاسخ‌دهنده */
    .respondent-card {
      display: grid; grid-template-columns: 1.1fr 1.6fr; gap: 16px;
      margin-top: 14px; padding: 14px 16px;
      border: 1px solid var(--line); border-radius: 12px; background: #f8f9fc;
    }
    .respondent-label { margin: 0; font-size: 10px; font-weight: 800; color: var(--ink-3); }
    .respondent-name { margin: 2px 0 4px; font-size: 18px; font-weight: 900; }
    .respondent-contact { margin: 0; font-size: 11px; font-weight: 700; color: var(--ink-2); }
    .respondent-facts { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; margin: 0; }
    .respondent-facts dt { font-size: 10px; font-weight: 800; color: var(--ink-3); }
    .respondent-facts dd { margin: 0; font-size: 11.5px; font-weight: 800; }

    /* KPI */
    .summary-grid { display: grid; grid-template-columns: 1.7fr repeat(3, 1fr); gap: 10px; margin-top: 14px; }
    .kpi {
      display: flex; flex-direction: column; justify-content: center; gap: 2px;
      padding: 12px 14px; border: 1px solid var(--line); border-radius: 12px; background: #fff;
    }
    .kpi-overall { flex-direction: row; align-items: center; gap: 14px; background: #fff; border-color: var(--line); }
    .kpi-value { margin: 0; font-size: 24px; font-weight: 900; line-height: 1.1; }
    .kpi-label { margin: 0; font-size: 10.5px; font-weight: 800; color: var(--ink-2); }
    .kpi-sub { margin: 2px 0 0; font-size: 10px; font-weight: 700; color: var(--ink-3); }
    .gauge { flex-shrink: 0; }

    .section-heading {
      margin: 0 0 6px; font-size: 13.5px; font-weight: 900; color: var(--ink);
      display: flex; align-items: center; gap: 8px;
    }
    .section-heading::before { content: ''; width: 4px; height: 16px; border-radius: 2px; background: var(--brand); }

    .narrative { margin-top: 16px; }
    .narrative p { margin: 0; font-size: 12px; font-weight: 600; color: var(--ink-2); line-height: 1.9; text-align: justify; }

    /* نیم‌رخ */
    .profile { margin-top: 18px; }
    .profile-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
    .legend { display: flex; gap: 12px; flex-wrap: wrap; font-size: 10px; font-weight: 700; color: var(--ink-2); }
    .legend-item { display: inline-flex; align-items: center; gap: 5px; }
    .legend-item i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
    .legend-marker { width: 2px !important; height: 12px !important; border-radius: 1px !important; background: var(--ink); }
    .profile-hint { margin: 2px 0 8px; font-size: 10px; font-weight: 700; color: var(--ink-3); }
    .profile-rows { display: grid; gap: 5px; }
    .profile-row {
      display: grid; grid-template-columns: 22px 1.4fr 2.2fr 30px 78px;
      align-items: center; gap: 8px;
    }
    .row-rank { font-size: 10px; font-weight: 900; color: var(--ink-3); text-align: center; }
    .row-title { font-size: 11px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .row-bar { position: relative; height: 12px; border-radius: 6px; background: #eef0f5; overflow: visible; }
    .row-bar-fill { height: 100%; border-radius: 6px; }
    .row-bar-marker {
      position: absolute; top: -3px; width: 2px; height: 18px; background: var(--ink); border-radius: 1px;
      transform: translateX(50%);
    }
    .row-score { font-size: 12px; font-weight: 900; text-align: center; }
    .row-badge {
      font-size: 9.5px; font-weight: 800; text-align: center;
      padding: 2px 6px; border-radius: 999px; border: 1px solid; white-space: nowrap;
    }

    /* تحلیل تفصیلی */
    .details { margin-top: 20px; }
    .details-heading { margin-bottom: 10px; }
    .section-block { margin-bottom: 14px; border: 1px solid var(--line); border-radius: 12px; overflow: hidden; break-inside: avoid; page-break-inside: avoid; }
    .section-head {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 12px; background: #f8f9fc; border-bottom: 1px solid;
    }
    .section-rank {
      width: 26px; height: 26px; border-radius: 8px; color: #fff;
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 900; flex-shrink: 0;
    }
    .section-head-main { flex: 1; min-width: 0; }
    .section-title { margin: 0; font-size: 13px; font-weight: 900; }
    .section-verdict { margin: 1px 0 0; font-size: 10.5px; font-weight: 800; }
    .section-scores { text-align: left; flex-shrink: 0; }
    .score-main { font-size: 20px; font-weight: 900; line-height: 1; }
    .score-max { font-size: 11px; font-weight: 700; color: var(--ink-3); }
    .score-sub { font-size: 9.5px; font-weight: 700; color: var(--ink-3); margin-top: 3px; line-height: 1.5; }
    .section-body { padding: 10px 12px 12px; }
    .section-narrative { margin: 0 0 6px; font-size: 11px; font-weight: 600; color: var(--ink-2); line-height: 1.8; }
    .section-desc { margin: 0 0 6px; font-size: 10.5px; color: var(--ink-3); font-weight: 600; line-height: 1.8; }
    .section-course { margin: 0 0 8px; font-size: 11px; font-weight: 800; color: var(--brand); }
    .section-course-key { color: var(--ink-3); font-weight: 700; }

    .answers { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    .answers th {
      text-align: right; font-weight: 800; color: var(--ink-3); font-size: 9.5px;
      padding: 5px 6px; border-bottom: 1px solid var(--line);
    }
    .answers td { padding: 5px 6px; border-bottom: 1px solid #f0f2f6; vertical-align: top; }
    .answers tr:last-child td { border-bottom: 0; }
    .col-idx { width: 22px; text-align: center !important; color: var(--ink-3); font-weight: 800; }
    .col-answer { width: 110px; font-weight: 800; }
    .col-score { width: 78px; }
    .q-text { font-weight: 600; line-height: 1.7; }
    .q-reverse { margin-inline-start: 4px; color: var(--ink-3); font-size: 10px; }
    .dots { display: inline-flex; gap: 3px; }
    .dots i { width: 9px; height: 9px; border-radius: 3px; display: inline-block; }

    /* پیشنهاد */
    .recommend { margin-top: 18px; padding: 14px 16px; border-radius: 12px; background: #f3f6fd; border: 1px solid #d9e2f7; break-inside: avoid; }
    .recommend-hint { margin: 0 0 8px; font-size: 10.5px; font-weight: 700; color: var(--ink-2); }
    .recommend-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
    .recommend-list li { display: flex; gap: 10px; align-items: flex-start; }
    .recommend-step {
      width: 24px; height: 24px; border-radius: 50%; background: var(--brand); color: #fff;
      display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 900; flex-shrink: 0;
    }
    .recommend-course { margin: 0; font-size: 12px; font-weight: 900; }
    .recommend-why { margin: 2px 0 0; font-size: 10.5px; font-weight: 700; color: var(--ink-2); display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .recommend-tag { font-size: 9.5px; font-weight: 800; padding: 1px 7px; border-radius: 999px; border: 1px solid; }

    .report-footer {
      margin-top: 22px; padding-top: 10px; border-top: 1px solid var(--line);
      display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap;
      font-size: 9.5px; font-weight: 700; color: var(--ink-3);
    }

    @page { size: A4; margin: 12mm 10mm; }
    @media print {
      .no-print { display: none !important; }
      .report-shell { background: #fff; }
      .report-page { width: auto; min-height: auto; margin: 0; padding: 0; box-shadow: none; }
      .report-header, .respondent-card, .summary-grid, .narrative, .profile, .recommend { break-inside: avoid; page-break-inside: avoid; }
      .details-heading { break-after: avoid; page-break-after: avoid; }
      a { text-decoration: none; color: inherit; }
    }
  `}</style>
);

export default AssessmentReportPrint;
